import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Filter } from 'lucide-react';
import { Card } from '../ui/Card';
import { SkillCard } from './SkillCard';
import { skillService, type Skill, type SkillCategory, type SkillFilter as SvcFilter } from '@/services/skills';
import { toast } from 'sonner';

const CATEGORIES: Array<{ value: SkillCategory | ''; label: string }> = [
  { value: '',            label: 'All' },
  { value: 'generate',    label: 'Generate' },
  { value: 'analyze',     label: 'Analyze' },
  { value: 'organize',    label: 'Organize' },
  { value: 'predict',     label: 'Predict' },
  { value: 'optimize',    label: 'Optimize' },
  { value: 'collaborate', label: 'Collaborate' },
];

interface SkillLibraryProps {
  /** When set, only skills whose id is in this list are shown. */
  skillIds?: string[];
  /** When set, only skills in these categories are shown. */
  restrictCategories?: SkillCategory[];
  /** Title for the section header. */
  title?: string;
  /** Subtitle / helper text. */
  description?: string;
  /** Optional className for the wrapping Card. */
  className?: string;
  /** Number of columns on large screens. */
  columns?: 2 | 3 | 4;
}

export const SkillLibrary: React.FC<SkillLibraryProps> = ({
  skillIds,
  restrictCategories,
  title = 'Skill Library',
  description = 'Every agent capability exposed by the Khiimori platform. Click Run to execute, or click Open to deep-link into the dedicated page.',
  className = '',
  columns = 3,
}) => {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [category, setCategory] = useState<SkillCategory | ''>('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchSkills = async () => {
    setLoading(true);
    try {
      const all = await skillService.listSkills({});
      let filtered = all;
      if (skillIds && skillIds.length > 0) {
        const set = new Set(skillIds);
        filtered = filtered.filter((s) => set.has(s.id));
      }
      if (restrictCategories && restrictCategories.length > 0) {
        const set = new Set(restrictCategories);
        filtered = filtered.filter((s) => set.has(s.category));
      }
      setSkills(filtered);
    } catch (e) {
      toast.error(`Failed to load skills: ${(e as Error).message}`);
      setSkills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSkills(); }, [skillIds?.join(','), restrictCategories?.join(',')]);

  const visible = useMemo(() => {
    let out = skills;
    if (category) out = out.filter((s) => s.category === category);
    if (search.trim()) {
      const needle = search.toLowerCase();
      out = out.filter(
        (s) =>
          s.name.toLowerCase().includes(needle) ||
          s.description.toLowerCase().includes(needle) ||
          s.id.toLowerCase().includes(needle),
      );
    }
    return out;
  }, [skills, category, search]);

  const handleChange = (updated: Skill) => {
    setSkills((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const colClass = columns === 2
    ? 'lg:grid-cols-2'
    : columns === 4
      ? 'lg:grid-cols-4'
      : 'lg:grid-cols-3';

  return (
    <Card className={`p-6 ${className}`}>
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-khiimori-text-primary">{title}</h3>
          <p className="text-sm text-khiimori-text-muted mt-1">{description}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
            <input
              type="text"
              placeholder="Search skills..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 bg-khiimori-bg-elevated/10 border border-khiimori-border/20 rounded-lg text-sm text-khiimori-text-primary placeholder:text-khiimori-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500/30"
            />
          </div>
          <button
            onClick={fetchSkills}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 bg-khiimori-bg-elevated/10 border border-khiimori-border/20 rounded-lg text-sm text-khiimori-text-primary hover:bg-khiimori-bg-elevated/20 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap mb-4">
        <Filter className="w-3.5 h-3.5 text-khiimori-text-muted mr-1" />
        {CATEGORIES.map((c) => (
          <button
            key={c.value || 'all'}
            onClick={() => setCategory(c.value)}
            className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
              category === c.value
                ? 'bg-blue-600 text-white'
                : 'bg-khiimori-bg-elevated/20 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/40'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading && skills.length === 0 ? (
        <div className={`grid grid-cols-1 md:grid-cols-2 ${colClass} gap-3`}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-32 rounded-xl bg-khiimori-bg-elevated/10 animate-pulse"
            />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-khiimori-text-muted text-center py-8">
          No skills match the current filter.
        </p>
      ) : (
        <div className={`grid grid-cols-1 md:grid-cols-2 ${colClass} gap-3`}>
          {visible.map((skill) => (
            <SkillCard key={skill.id} skill={skill} onChange={handleChange} />
          ))}
        </div>
      )}
    </Card>
  );
};

export default SkillLibrary;
